import "@radix-ui/themes/styles.css";
import "./styles/main.scss";

import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { RouterProvider } from "react-router-dom";
import { Flex } from "@radix-ui/themes";
import { JSX, useEffect, useMemo, useState } from "react";

import { BrowserInfo } from "./utils/helperFunctions/getBrowserInfo/BrowserInfo";
import ErrorBoundary from "./components/ui/ErrorBoundary/ErrorBoundary";
import { brand } from "./constants/brandVariables/brandVariables";
import AppFallback from "./components/ui/AppFallback/AppFallback";
import { ThemeProvider } from "./contexts/ThemeProvider";
import Loader from "./components/ui/Loader/Loader";
import { auth } from "./api/firebaseConfig";
import appRouter from "./routes/appRouter";

function AppContent() {
  const router = useMemo(() => appRouter(), []);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const email = `${import.meta.env.VITE_TRACKER_AUTOLOGIN_EMAIL}-${
      brand.tenantId
    }`;
    const pass = import.meta.env.VITE_TRACKER_AUTOLOGIN_PASS;

    const ensureSignedIn = async () => {
      if (auth.currentUser?.uid) return;
      try {
        await signInWithEmailAndPassword(auth, email, pass);
      } catch (error) {
        const code = (error as { code?: string }).code;
        if (code === "auth/user-not-found") {
          try {
            await createUserWithEmailAndPassword(auth, email, pass);
          } catch (createErr) {
            console.error("[autologin] create failed:", createErr);
          }
        } else {
          console.error("[autologin] sign-in failed:", code, error);
        }
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthReady(true);
      } else {
        ensureSignedIn();
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    document.title = `${window.location.pathname.replace(
      "/",
      " ",
    )} - leaderboard`;
  }, []);

  if (window.location.pathname.includes("test")) {
    return <BrowserInfo />;
  }

  if (!authReady) {
    return (
      <Flex align="center" justify="center" width="100vw" height="100vh">
        <Loader />
      </Flex>
    );
  }

  return <RouterProvider router={router} />;
}

function App(): JSX.Element {
  // No brand resolves from the URL (unknown/root path) → ThemeProvider would
  // crash reading brand.theme. Show a recoverable fallback instead of a blank
  // screen on the kiosk.
  if (!brand) {
    return (
      <AppFallback
        title="Leaderboard not found"
        message="This screen isn’t configured for a known brand. Please check the URL."
      />
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
